<template lang="pug">
.detail-pedido-tienda-view
  .loader(v-if="loading")
    b-spinner(variant="primary")

  template(v-else-if="pedido")
    .container-detail
      //- Header Navigation
      .header-pedido.d-none.d-md-flex
        .title-section(@click="$router.push('/pedidos-tienda')")
          b-icon.back-arrow(icon="arrow-left-square-fill")
          h4.title Detalle Pedido \#{{ pedido.id }}

      .breadcrumbs-pedido
        span.text-muted Inicio / Pedidos / Pedidos de la Tienda /
        span.text-dark Detalle Pedido \#{{ pedido.id }}

      //- Blue top bar
      .top-bar-pedido
        h4.top-bar-title Detalle Pedido \#{{ pedido.id }}
        .top-bar-actions
          b-badge.pedido-badge(
            v-if="origenBadge"
            :style="badgeStyle(origenBadge)"
          ) {{ origenBadge.text }}
          b-badge.pedido-badge(
            :style="badgeStyle(estadoPagoBadge(pedido.estado_pago))"
          )
            b-icon.mr-1(:icon="estadoPagoBadge(pedido.estado_pago).icon")
            | {{ estadoPagoBadge(pedido.estado_pago).text }}
          b-button.btn-download(
            :disabled="!pedido.tiene_boleta"
            @click="descargarDocumento"
          )
            b-icon.mr-2(icon="file-earmark-arrow-down-fill")
            span {{ pedido.tipo_documento === 2 ? 'Descargar Factura' : 'Descargar Boleta' }}

      //- Two-column content layout
      .content-layout
        //- Left panel
        .left-panel
          .main-card
            //- Client Data Section
            .detail-section
              .section-subtitle
                b-icon(icon="person-fill")
                span Datos del Cliente

              .data-grid
                .data-item
                  label R.U.T
                  p.blue-text {{ pedido.cliente.rut }}
                .data-item
                  label Nombre
                  p {{ pedido.cliente.nombre }}
                .data-item
                  label Apellido
                  p {{ pedido.cliente.apellido }}
                .data-item
                  label Correo Electrónico
                  p {{ pedido.cliente.email }}

                .data-item
                  label Teléfono
                  p {{ pedido.cliente.telefono }}

            //- Shipping Data Section
            .detail-section
              .section-subtitle
                b-icon(icon="truck")
                span Datos de Envío

              .data-grid
                .data-item
                  label Tipo de Envio
                  p.blue-text {{ pedido.envio.tipo_envio_display }}
                .data-item
                  label Región
                  p {{ pedido.envio.region || '-' }}
                .data-item
                  label Comuna
                  p {{ pedido.envio.comuna || '-' }}
                .data-item
                  label Dirección
                  p {{ pedido.envio.direccion || '-' }}

            //- Billing Data Section
            .detail-section(v-if="pedido.facturacion")
              .section-subtitle
                b-icon(icon="receipt")
                span Datos de Facturación

              .data-grid
                .data-item
                  label R.U.T. Empresa
                  p.blue-text {{ pedido.facturacion.rut_contribuyente }}
                .data-item
                  label Documento
                  p {{ pedido.facturacion.tipo_documento_display }}
                .data-item
                  label Razón Social
                  p {{ pedido.facturacion.razon_social }}
                .data-item
                  label Giro
                  p {{ pedido.facturacion.giro }}

            //- Equipment Section
            .detail-section
              .section-subtitle
                b-icon(icon="tools")
                span Equipos Comprados

              b-table(
                :items="pedido.productos"
                :fields="productosFields"
                responsive
              )
                template(#cell(ot)="data")
                  router-link.blue-text(
                    v-if="data.item.ot"
                    :to="`/orden/${data.item.ot}/`"
                  ) OT-{{ data.item.ot }}
                  span(v-else) -

                template(#cell(precio_unitario)="data")
                  span {{ data.item.precio_unitario | formatCurrency }}

        //- Right panel — Resumen
        .right-panel
          .resumen-card
            .detail-section
              .section-subtitle
                b-icon(icon="cash-stack")
                span Resumen del Pago

              .data-item.mb-3
                label Fecha de Pago
                p {{ fechaPagoLabel }}

              .totals-summary-card
                .total-line
                  span.label Sub Total
                  span.value {{ pedido.resumen_pago.sub_total | formatCurrency }}
                .total-line
                  span.label I.V.A.
                  span.value {{ pedido.resumen_pago.iva | formatCurrency }}
                .total-line
                  span.label Descuento
                  span.value - {{ pedido.resumen_pago.descuento | formatCurrency }}
                .total-line(v-if="pedido.resumen_pago.garantia_extendida > 0")
                  span.label Garantía Extendida
                  span.value {{ pedido.resumen_pago.garantia_extendida | formatCurrency }}
                .total-line
                  span.label Embalaje y Envío
                  span.value {{ pedido.resumen_pago.embalaje_envio | formatCurrency }}

                hr.total-divider

                .total-line.final-total
                  span.label Total
                  span.value {{ pedido.resumen_pago.total | formatCurrency }}

  .not-found(v-else-if="!loading")
    .text-center.p-5
      h4 Pedido no encontrado
      b-button(variant="primary" @click="$router.push('/pedidos-tienda')") Volver al listado
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'PedidosTiendaDetailView',
  data() {
    return {
      loading: true,
      pedido: null,
      productosFields: [
        { key: 'ot', label: 'OT' },
        { key: 'equipo_nombre', label: 'Equipo' },
        { key: 'serial_number', label: 'Número de Serie' },
        { key: 'precio_unitario', label: 'Precio Unitario' },
      ],
    }
  },
  computed: {
    origenBadge() {
      const map = {
        tienda: { text: 'Tienda Online', bg: '#f3e8ff', color: '#6b21a8' },
        center: { text: 'Center', bg: '#e1effe', color: '#1e429f' },
        cotizacion: { text: 'Cotización OVE', bg: '#d7d7d7', color: '#444444' },
      }
      return (this.pedido && map[this.pedido.origen]) || null
    },

    fechaPagoLabel() {
      const fecha =
        this.pedido &&
        this.pedido.resumen_pago &&
        this.pedido.resumen_pago.fecha_pago
      return fecha ? moment(fecha).format('DD/MM/YYYY') : '-'
    },
  },
  methods: {
    ...mapActions('PedidosTiendaStore', [
      'fetchPedidoDetail',
      'downloadDocumento',
    ]),

    async fetchData() {
      this.loading = true
      try {
        const id = this.$route.params.pedido_id
        this.pedido = await this.fetchPedidoDetail(id)
      } catch (error) {
        console.error('Error fetching pedido detail:', error)
        this.pedido = null
      } finally {
        this.loading = false
      }
    },

    async descargarDocumento() {
      try {
        const tipoDocumento =
          this.pedido.tipo_documento === 2 ? 'factura' : 'boleta'
        await this.downloadDocumento({ id: this.pedido.id, tipoDocumento })
      } catch (err) {
        this.$bvToast.toast('Error al descargar el documento', {
          variant: 'danger',
        })
      }
    },

    estadoPagoBadge(estado) {
      const map = {
        1: {
          text: 'Pendiente',
          bg: '#fef3c7',
          color: '#92400e',
          icon: 'clock-fill',
        },
        2: {
          text: 'Aceptado',
          bg: '#e6f4ea',
          color: '#137333',
          icon: 'check-circle-fill',
        },
        3: {
          text: 'Rechazado',
          bg: '#fde8e8',
          color: '#ff3838',
          icon: 'x-circle-fill',
        },
      }
      return (
        map[estado] || { text: '—', bg: '#e9ecef', color: '#525252', icon: '' }
      )
    },

    badgeStyle(badge) {
      return {
        backgroundColor: badge.bg,
        color: badge.color,
      }
    },
  },
  async mounted() {
    await this.fetchData()
  },
}
</script>

<style lang="scss" scoped>
@import '@/style/colors.scss';

.detail-pedido-tienda-view {
  background-color: #f5f6fa;
  min-height: 100vh;
  padding-bottom: 50px;
}

.container-detail {
  padding: 20px;
}

.header-pedido {
  display: flex;
  align-items: center;
  height: 4rem;

  .title-section {
    display: flex;
    align-items: center;
    gap: 15px;
    cursor: pointer;

    .back-arrow {
      color: $samtek-yellow;
      font-size: 32px;
    }

    .title {
      margin: 0;
      font-weight: bold;
      color: $samtek-black;
    }
  }
}

.breadcrumbs-pedido {
  margin-bottom: 25px;
  font-size: 14px;
}

.top-bar-pedido {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #203859;
  border-radius: 8px;
  padding: 12px 24px;
  margin-bottom: 20px;

  .top-bar-title {
    color: #ffffff;
    font-weight: 700;
    font-size: 20px;
    margin: 0;
  }

  .top-bar-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .btn-download {
    background-color: #f5f6fa;
    border: 1px solid $samtek-yellow;
    color: $samtek-black;
    border-radius: 8px;
    font-weight: 500;
    font-size: 15px;
    display: flex;
    align-items: center;

    &:hover:not(:disabled) {
      background-color: darken(#f5f6fa, 2%);
    }
  }
}

.pedido-badge {
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
}

.content-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.left-panel {
  flex: 1;
  min-width: 0;
}

.right-panel {
  width: 386px;
  flex-shrink: 0;
}

.main-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
}

.resumen-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
}

.detail-section {
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }

  .section-subtitle {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;

    .b-icon {
      font-size: 24px;
      color: #23201c;
    }

    span {
      font-family: 'Noto Sans', sans-serif;
      font-weight: 700;
      font-size: 20px;
      color: #23201c;
    }
  }
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.data-item {
  label {
    display: block;
    font-family: 'Noto Sans', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: #525252;
    margin-bottom: 2px;
  }

  p {
    font-family: 'Noto Sans', sans-serif;
    font-size: 15px;
    color: #525252;
    margin: 0;

    &.blue-text {
      color: #1d4ed8;
      font-weight: 700;
    }
  }
}

.blue-text {
  color: #1d4ed8;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    color: #1e40af;
  }
}

.totals-summary-card {
  background: rgba(244, 194, 53, 0.1);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .total-line {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .label {
      font-family: 'Noto Sans', sans-serif;
      font-weight: 700;
      font-size: 18px;
      color: #525252;
    }

    .value {
      font-family: 'Noto Sans', sans-serif;
      font-size: 15px;
      color: #525252;
    }

    &.final-total {
      .label {
        font-size: 20px;
        color: #1d4ed8;
      }
      .value {
        font-size: 20px;
        font-weight: 700;
        color: #1d4ed8;
      }
    }
  }

  .total-divider {
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 10px 0;
  }
}

@media (max-width: 1200px) {
  .content-layout {
    flex-direction: column;
  }

  .right-panel {
    width: 100%;
  }

  .data-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .top-bar-pedido {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .data-grid {
    grid-template-columns: 1fr;
  }
}
</style>
